import cx from 'classnames';
import { graphql } from 'gatsby';
import Image from 'gatsby-image';
import React from 'react';
import { trackEvent } from '../analytics';
import { Card } from '../components/card';
import { Dialog } from '../components/dialog';
import { CollectionIcon } from '../components/icon/collection-icon';
import { LeftIcon } from '../components/icon/left-icon';
import { RightIcon } from '../components/icon/right-icon';
import { useLocalize } from '../components/localize-context';
import { Seo } from '../components/seo';
import { isFilledArray } from '../lib';
import styles from './album.module.scss';

export default function Album({ data: { allPrismicAlbum } }) {
  const localize = useLocalize();

  return (
    <>
      <Seo title={localize('Album')} />
      <div className="container mx-auto max-w-6xl py-6">
        <h1 className="text-3xl px-4">{localize('Album')}</h1>
        <div className="py-6 sm:px-4 text-center">
          {allPrismicAlbum.nodes
            .filter(node => node.data.photo && isFilledArray(node.data.photo))
            .map(node => (
              <AlbumViewer
                title={node.data.album_title.text}
                photos={node.data.photo}
                id={node.uid}
                key={node.uid}
              />
            ))}
        </div>
      </div>
    </>
  );
}

export const pageQuery = graphql`
  query AlbumQuery($lang: String!) {
    allPrismicAlbum(
      filter: { lang: { eq: $lang } }
      sort: { fields: data___order }
    ) {
      nodes {
        uid
        data {
          album_title {
            text
          }
          photo {
            caption {
              text
            }
            image {
              url
              alt
              localFile {
                childImageSharp {
                  fluid(maxWidth: 800) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
              preview {
                url
                alt
                localFile {
                  childImageSharp {
                    fixed(width: 300) {
                      ...GatsbyImageSharpFixed_withWebp
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const AlbumViewer = ({ title, photos, id }) => {
  const localize = useLocalize();
  const [showList, setShowList] = React.useState(false);
  const [photoIndex, setPhotoIndex] = React.useState(0);

  const shownPhoto = photos[photoIndex];
  const shownImage = shownPhoto.image;
  const imagePreview = shownImage.preview;

  return (
    <Card className="mb-4 sm:mr-4 inline-block">
      <h2 className="text-lg text-left pb-2 px-1">{title}</h2>
      <div className="text-center relative">
        <a
          onClick={ev => {
            ev.preventDefault();
            setShowList(true);
            trackEvent({
              category: 'button',
              action: 'click',
              label: `view ${title}`,
            });
          }}
          id={id}
          href={`#${id}`}
        >
          <Image
            fixed={imagePreview.localFile.childImageSharp.fixed}
            alt={shownPhoto.caption.text || imagePreview.alt}
          />
        </a>
        <CollectionIcon
          fillOpacity="0.6"
          fill="#ffffff"
          className="absolute absolute-bottom-right"
        />
      </div>
      <Dialog
        title={title}
        isOpen={showList}
        onDismiss={() => setShowList(false)}
        className="w-full xs:w-10/12"
        compact
      >
        <Image
          fluid={shownImage.localFile.childImageSharp.fluid}
          alt={shownPhoto.caption.text || shownImage.alt}
          key={photoIndex}
        />
        <div className="flex justify-between">
          <button
            onClick={() => setPhotoIndex(i => i - 1)}
            disabled={photoIndex === 0}
            className={cx(
              'py-1 px-2 text-blue-500 bg-white rounded-full inline-flex justify-center items-center',
              styles.prevBtn,
              photoIndex === 0 && 'invisible'
            )}
          >
            <LeftIcon className="fill-current inline-block" width={40} />
            <span className="sr-only">{localize('Previous Photo')}</span>
          </button>
          <button
            onClick={() => setPhotoIndex(i => i + 1)}
            disabled={photoIndex === photos.length - 1}
            className={cx(
              'py-1 px-2 text-blue-500 bg-white rounded-full inline-flex justify-center items-center',
              styles.nextBtn,
              photoIndex === photos.length - 1 && 'invisible'
            )}
          >
            <span className="sr-only">{localize('Next Photo')}</span>
            <RightIcon className="fill-current inline-block" width={40} />
          </button>
        </div>
      </Dialog>
    </Card>
  );
};
